/**
 * Malcriados · base del sitio.
 *
 * 1. Ventanas propias (<dialog>): abrir con [data-mc-abrir="id"], cerrar con [data-mc-cerrar]
 *    o tocando fuera.
 * 2. Fecha de llegada segun region: rellena cualquier elemento con
 *    [data-mc-llegada="Texto con [lugar], [fechas] o [fechas-cortas]"].
 *
 * 3. Selector de region ("Cambiar región"): cualquier bloque con [data-mc-region] (la barra
 *    superior y el bloque de envio de producto) abre su lista y cambia la region.
 *
 * API para otras secciones (window.MC):
 *   MC.region            -> region elegida (se guarda en el navegador)
 *   MC.elegirRegion(n)   -> cambia la region y avisa con el evento "mc:region"
 *   MC.regiones          -> [[nombre, diasMin, diasMax], ...]
 *   MC.llegada(n?)       -> { region, lugar, desde, hasta, fechas, cortas } o null
 *   MC.dinero(centavos)  -> "$55.000"
 */

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const CLAVE_REGION = 'mc_region';

function leerConfiguracion() {
  const nodo = document.getElementById('mc-regiones');
  if (!nodo) return null;
  try {
    return JSON.parse(nodo.textContent || 'null');
  } catch {
    return null;
  }
}

const configuracion = leerConfiguracion();

function leerRegionGuardada() {
  try {
    return window.localStorage.getItem(CLAVE_REGION);
  } catch {
    return null;
  }
}

/**
 * @param {Date} desde
 * @param {number} dias
 * @param {boolean} soloHabiles
 */
function sumarDias(desde, dias, soloHabiles) {
  const fecha = new Date(desde.getFullYear(), desde.getMonth(), desde.getDate());
  let faltan = Math.max(0, Math.round(dias));
  while (faltan > 0) {
    fecha.setDate(fecha.getDate() + 1);
    const dia = fecha.getDay();
    if (!soloHabiles || (dia !== 0 && dia !== 6)) faltan -= 1;
  }
  return fecha;
}

/**
 * @param {Date} desde
 * @param {Date} hasta
 */
function textoFechas(desde, hasta) {
  const mesDesde = MESES[desde.getMonth()];
  const mesHasta = MESES[hasta.getMonth()];
  if (desde.getTime() === hasta.getTime()) {
    return {
      fechas: `el ${desde.getDate()} de ${mesDesde}`,
      cortas: `${desde.getDate()} ${mesDesde.slice(0, 3)}`,
    };
  }
  if (mesDesde === mesHasta) {
    return {
      fechas: `del ${desde.getDate()} al ${hasta.getDate()} de ${mesHasta}`,
      cortas: `${desde.getDate()}–${hasta.getDate()} ${mesHasta.slice(0, 3)}`,
    };
  }
  return {
    fechas: `del ${desde.getDate()} de ${mesDesde} al ${hasta.getDate()} de ${mesHasta}`,
    cortas: `${desde.getDate()} ${mesDesde.slice(0, 3)} – ${hasta.getDate()} ${mesHasta.slice(0, 3)}`,
  };
}

const MC = {
  /** @type {Array<[string, number, number]>} */
  regiones: configuracion?.regiones ?? [],

  get region() {
    const guardada = leerRegionGuardada();
    if (guardada && MC.regiones.some((fila) => fila[0] === guardada)) return guardada;
    return configuracion?.porDefecto ?? MC.regiones[0]?.[0] ?? '';
  },

  /** @param {string} nombre */
  elegirRegion(nombre) {
    if (!MC.regiones.some((fila) => fila[0] === nombre)) return;
    try {
      window.localStorage.setItem(CLAVE_REGION, nombre);
    } catch {
      /* sin almacenamiento: la eleccion dura lo que dure la pagina */
    }
    document.dispatchEvent(new CustomEvent('mc:region', { detail: { region: nombre } }));
  },

  /** @param {string} [nombre] */
  llegada(nombre) {
    const region = nombre ?? MC.region;
    const fila = MC.regiones.find((f) => f[0] === region);
    if (!fila) return null;
    const hoy = new Date();
    const soloHabiles = configuracion?.habiles !== false;
    const desde = sumarDias(hoy, fila[1], soloHabiles);
    const hasta = sumarDias(hoy, Math.max(fila[1], fila[2]), soloHabiles);
    return {
      region,
      lugar: region === 'Metropolitana' ? 'Santiago' : region,
      desde,
      hasta,
      ...textoFechas(desde, hasta),
    };
  },

  /** @param {number} centavos */
  dinero(centavos) {
    const pesos = Math.round(centavos / 100);
    return '$' + String(pesos).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  },
};

window.MC = MC;

function pintarLlegadas() {
  const llegada = MC.llegada();
  if (!llegada) return;
  for (const nodo of document.querySelectorAll('[data-mc-llegada]')) {
    const plantilla = nodo.getAttribute('data-mc-llegada') || '';
    nodo.textContent = plantilla
      .replaceAll('[lugar]', llegada.lugar)
      .replaceAll('[region]', llegada.region)
      .replaceAll('[fechas-cortas]', llegada.cortas)
      .replaceAll('[fechas]', llegada.fechas);
  }
}

pintarLlegadas();
document.addEventListener('mc:region', pintarLlegadas);
document.addEventListener('mc:contenido', pintarLlegadas);

/* ---------- Selector de region ----------
 * Vive aqui y no en mc-pdp.js: la barra superior sale en todo el sitio, y el enlace
 * "Cambiar región" tiene que funcionar donde sea que se vea, no solo en producto. */

/** @param {Element | null} nodo @param {boolean} oculto */
function ocultarNodo(nodo, oculto) {
  if (nodo instanceof HTMLElement) nodo.hidden = oculto;
}

function pintarSelectoresRegion() {
  if (!MC.regiones.length) return;
  for (const bloque of document.querySelectorAll('[data-mc-region]')) {
    const lista = bloque.querySelector('[data-mc-region-lista]');
    if (lista instanceof HTMLSelectElement) {
      if (lista.options.length === 0) {
        for (const fila of MC.regiones) lista.add(new Option(fila[0], fila[0]));
      }
      lista.value = MC.region;
    }
    /* El boton nace oculto: sin JavaScript no hay nada que abrir. */
    ocultarNodo(bloque.querySelector('[data-mc-region-abrir]'), false);
  }
}

function cerrarSelectoresRegion() {
  for (const bloque of document.querySelectorAll('[data-mc-region]')) {
    ocultarNodo(bloque.querySelector('[data-mc-region-panel]'), true);
    bloque.querySelector('[data-mc-region-abrir]')?.setAttribute('aria-expanded', 'false');
  }
}

pintarSelectoresRegion();
document.addEventListener('mc:region', pintarSelectoresRegion);
document.addEventListener('mc:contenido', pintarSelectoresRegion);
/* En el editor de temas las secciones se vuelven a pintar. */
document.addEventListener('shopify:section:load', pintarSelectoresRegion);

document.addEventListener('click', (evento) => {
  const abrir = evento.target instanceof Element ? evento.target.closest('[data-mc-region-abrir]') : null;
  if (!abrir) return;
  const panel = abrir.closest('[data-mc-region]')?.querySelector('[data-mc-region-panel]');
  if (!(panel instanceof HTMLElement)) return;
  const abierto = !panel.hidden;
  cerrarSelectoresRegion();
  panel.hidden = abierto;
  abrir.setAttribute('aria-expanded', String(!abierto));
  if (!abierto) panel.querySelector('select')?.focus();
});

document.addEventListener('change', (evento) => {
  if (!(evento.target instanceof HTMLSelectElement) || !evento.target.hasAttribute('data-mc-region-lista')) return;
  MC.elegirRegion(evento.target.value);
  cerrarSelectoresRegion();
});

/* ------------------------------------------------------------
 * Menu de celular: pantalla completa debajo de la barra y del encabezado.
 * La pagina se lleva arriba y se fija mientras esta abierto; al cerrarlo vuelve a su lugar.
 * ------------------------------------------------------------ */
let posicionAntesDelMenu = 0;

function menuAbierto() {
  const menu = document.getElementById('mc-menu');
  return menu instanceof HTMLDialogElement && menu.open;
}

function abrirMenu() {
  const menu = document.getElementById('mc-menu');
  if (!(menu instanceof HTMLDialogElement) || menu.open) return;
  posicionAntesDelMenu = window.scrollY;
  window.scrollTo({ top: 0, behavior: 'instant' });
  const encabezado = document.querySelector('.mc-encabezado');
  const base = encabezado ? Math.round(encabezado.getBoundingClientRect().bottom) : 0;
  menu.style.setProperty('--mc-menu-top', `${base}px`);
  document.documentElement.setAttribute('data-mc-menu-abierto', '');
  menu.show();
  /* show() manda el foco al primer enlace y dibuja un recuadro sobre "Inicio": el foco se queda en el propio menu. */
  menu.focus({ preventScroll: true });
  for (const boton of document.querySelectorAll('[data-mc-menu-boton]')) boton.setAttribute('aria-expanded', 'true');
}

function cerrarMenu() {
  const menu = document.getElementById('mc-menu');
  if (!(menu instanceof HTMLDialogElement) || !menu.open) return;
  menu.close();
}

function alternarMenu() {
  if (menuAbierto()) cerrarMenu();
  else abrirMenu();
}

/* Cualquier cierre (boton, enlace, tecla Escape) termina aqui. */
document.getElementById('mc-menu')?.addEventListener('close', () => {
  document.documentElement.removeAttribute('data-mc-menu-abierto');
  for (const boton of document.querySelectorAll('[data-mc-menu-boton]')) boton.setAttribute('aria-expanded', 'false');
  window.scrollTo({ top: posicionAntesDelMenu, behavior: 'instant' });
});

document.addEventListener('keydown', (evento) => {
  if (evento.key === 'Escape' && menuAbierto()) {
    evento.preventDefault();
    cerrarMenu();
  }
});

/* Si la pantalla pasa a ancho de computador con el menu abierto, se cierra. */
window.matchMedia('(min-width: 990px)').addEventListener('change', (cambio) => {
  if (cambio.matches) cerrarMenu();
});

document.addEventListener('click', (evento) => {
  const origen = evento.target instanceof Element ? evento.target : null;
  if (!origen) return;

  const abrir = origen.closest('[data-mc-abrir]');
  if (abrir && abrir.hasAttribute('data-mc-menu-boton')) {
    evento.preventDefault();
    alternarMenu();
    return;
  }
  if (abrir) {
    const ventana = document.getElementById(abrir.getAttribute('data-mc-abrir') || '');
    if (ventana instanceof HTMLDialogElement && !ventana.open) {
      evento.preventDefault();
      ventana.showModal();
    }
    return;
  }

  const cerrar = origen.closest('[data-mc-cerrar]');
  if (cerrar) {
    const ventana = cerrar.closest('dialog');
    if (ventana && ventana.id === 'mc-menu') cerrarMenu();
    else ventana?.close();
    return;
  }

  /* Tocar un enlace del menu lo cierra antes de ir a la pagina. */
  if (origen.closest('#mc-menu a')) cerrarMenu();

  /* Toque fuera del contenido: en un <dialog> modal ese toque llega al propio dialog. */
  if (origen instanceof HTMLDialogElement && origen.classList.contains('mc') && origen.open) {
    const caja = origen.getBoundingClientRect();
    const fuera =
      evento.clientX < caja.left || evento.clientX > caja.right || evento.clientY < caja.top || evento.clientY > caja.bottom;
    if (fuera) origen.close();
  }
});

